import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Avatar, Chip, ListItemIcon, ListItemText, Typography, useMediaQuery, Box } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import { MENU_OPEN, SET_MENU } from '../../../../store/actions';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

/**
 * Styles
 */
const useStyles = makeStyles((theme) => ({
    /**
     * List Icon
     */
    listIcon: {
        minWidth: '18px',
        marginTop: 0,
        marginBottom: 0
    },
    /**
     * List Custom Icon Sub
     */
    listCustomIconSub: {
        width: '6px',
        height: '6px'
    },
    /**
     * List Custom Icon Sub Active
     */
    listCustomIconSubActive: {
        width: '4px',
        height: '4px'
    },
    listItem: {
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0
    },
    listItemNoBack: {
        marginBottom: '2px',
        backgroundColor: 'transparent !important',
        paddingTop: '1px',
        paddingBottom: '1px',
        alignItems: 'flex-start'
    },
    subMenuCaption: {
        ...theme.typography.subMenuCaption
    },
    drawerPaper: {
        width: 260,
        background: '#fff',
        borderRight: '1px solid #e5e7eb'
    }
}));

/**
 * Nav Item
 */
const NavItem = ({ item, level }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const IconComponent = item.icon;

    const itemIcon = IconComponent ? (
        <IconComponent
            size={22}
            stroke={1.8}
            color={
                customization.isOpen.includes(item.id)
                    ? '#1e73d8'
                    : '#444'
            }
        />
    ) : null;

    let itemIconClass = !item.icon ? classes.listIcon : classes.menuIcon;
    itemIconClass = customization.navType === 'nav-dark' ? [itemIconClass, classes.listCustomIcon].join(' ') : itemIconClass;

    let itemTarget = '';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = { component: React.forwardRef((props, ref) => <Link {...props} to={item.url} />) };
    if (item.external) {
        listItemProps = { component: 'a', href: item.url };
    }

    /**
     * Handle item click
     */
    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id: id });
        matchesSM && dispatch({ type: SET_MENU, opened: false });
    };

    /**
     * Check if item is active
     */
    React.useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
    }, []);

    return (
        /**
         * List Item Button
         */
        <ListItemButton
            disableGutters
            {...listItemProps}
            disabled={item.disabled}
            onClick={() => itemHandler(item.id)}
            selected={customization.isOpen.findIndex((id) => id === item.id) > -1}
            disableRipple
            sx={{
                padding: '6px 8px !important',
                margin: 0,
                display: 'grid',
                gridTemplateColumns: '28px 1fr',
                alignItems: 'center',
                borderRadius: 8,

                '& .MuiListItemIcon-root': {
                    display: 'none'
                },
                '& .MuiListItemText-root': {
                    margin: 0
                },

                '&.Mui-selected': {
                    backgroundColor: '#e8f0fe',
                    color: '#1e73d8'
                },
                '&:hover': {
                    backgroundColor: '#f3f6fb'
                }
            }}
        >
            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'inherit' }}>
                {itemIcon}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        letterSpacing: '0.2px'
                    }}
                >
                    {item.title}
                </Typography>
            </Box>
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

/**
 * Nav Item component
 */
NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
